/**
 * useBrand — React hook returning the current active brand and re-rendering
 * the consuming component whenever the active brand changes.
 *
 * Usage:
 *   const brand = useBrand();
 *   return <h1>{brand.name}</h1>;
 */

import { useContext, useEffect, useState } from 'react';
import { brand, subscribeBrandChange } from './index';
import { BrandScopeContext } from './BrandScope';
import type { BrandConfig } from './types';

export function useBrand(): BrandConfig {
  // A <BrandScope> above us means this subtree is deliberately rendered under
  // someone else's brand — the White-Label editor previewing another tenant's
  // pages. Outside one the context is null and nothing changes.
  const scoped = useContext(BrandScopeContext);
  const [, bump] = useState(0);
  // Still subscribe even when scoped: the subscription is what keeps the hook
  // cheap and consistent, and a scoped consumer re-renders anyway whenever the
  // provider hands it a new brand object.
  useEffect(() => subscribeBrandChange(() => bump(v => v + 1)), []);
  return scoped ?? brand;
}
